import {
  Stepper,
  Step,
  StepButton,
  Paper,
  Container,
  styled,
  Grid,
  Stack,
} from "@mui/material";
import React, { useContext } from "react";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { brand, Button, paletteVar, Typography } from "@platform/design-system";
import { CLIENT_ACTION_VALUES, RFP_ACTION_VALUES } from "@platform/rfp-core";

import { scrollToTop } from "../../utils";
import { submitEvent } from "../../utils/tracking";

import { getTimeFromLastUpdate } from "./utils";
import { RFP_ACTION_TYPES } from "./constants";
import { RfpContext } from "./contexts";
import { RFPBanner } from "./components";

const RFPStepper = styled(Stepper)(({ theme }) => {
  const purple = brand("primary")(theme);
  return {
    ".MuiStepIcon-root, .Mui-completed .MuiStepIcon-root": {
      color: paletteVar("text-disabled", "rgba(33,33,33,0.38)"),
    },
    ".MuiStepIcon-text": {
      fill: theme.palette.common.white,
    },
    ".Mui-active, .color-step": {
      color: `${purple} !important`,
    },
    ".color-step": {
      color: purple,
      ".MuiStepIcon-root": {
        color: purple,
      },
    },
    ".invalid-step": {
      cursor: "not-allowed",
      button: {
        pointerEvents: "none",
      },
    },
    ".MuiStepLabel-label.Mui-completed": {
      color: purple,
    },
  };
});

const RFPStepperPaper = styled(Paper)(() => ({
  position: "fixed",
  zIndex: 2,
  width: "100%",
}));

const steps = [
  { name: "RFP Details" },
  { name: "Quote Coverages" },
  { name: "Coverage Details" },
  { name: "Carriers" },
  { name: "Documents" },
  { name: "Review" },
];

const { IS_RFP_FORM_VALID, NAME, CLIENT_TYPE } = CLIENT_ACTION_VALUES;
const {
  ACTIVE_STEP,
  ARE_COVERAGES_VALID,
  SELECTED_COVERAGES,
  SELECTED_CARRIERS,
  IS_CENTRO_CARRIER_SELECTED,
  LAST_MODIFIED,
  ID,
  ARE_DOCUMENTS_VALID,
} = RFP_ACTION_VALUES;
const { UPDATE_STEP } = RFP_ACTION_TYPES;

const CustomStepper = () => {
  const {
    rfp,
    form,
    handleSaveDraft,
    setRfp,
    isSavingDraftRfp,
    setIsDiscardDialogOpen,
  } = useContext(RfpContext);
  const clientName = form[NAME];
  const activeStep = rfp[ACTIVE_STEP];

  const isStepValid = {
    0: rfp[IS_RFP_FORM_VALID],
    1: rfp[SELECTED_COVERAGES].length,
    2: rfp[SELECTED_COVERAGES].length && rfp[ARE_COVERAGES_VALID],
    3: rfp[SELECTED_CARRIERS].length || rfp[IS_CENTRO_CARRIER_SELECTED],
    4: rfp[ARE_DOCUMENTS_VALID] || activeStep > 4,
    5: false,
  };

  const areBackStepsValid = (current) =>
    Object.values(isStepValid)
      ?.slice(0, current)
      ?.every((s) => !!s);

  const handleStep = (step) => () => {
    scrollToTop();
    if (areBackStepsValid(step)) {
      setRfp({ type: UPDATE_STEP, value: step });
    }
  };

  const lastModified = rfp[LAST_MODIFIED];
  const timeFromLastUpdate = getTimeFromLastUpdate(lastModified);

  const isStepComplete = (index) =>
    index !== activeStep && !!areBackStepsValid(index) && !!isStepValid[index];

  const getStepClassName = (index) => {
    if (isStepValid[index] && areBackStepsValid(index) && activeStep >= index)
      return "color-step";

    return !areBackStepsValid(index) ? "invalid-step" : "";
  };

  return (
    <RFPStepperPaper>
      <RFPBanner>
        <Container>
          <Typography>RFP Submission for {clientName}</Typography>
        </Container>
      </RFPBanner>
      <Container disableGutters={true}>
        <Grid container py={3} alignItems="center">
          <Grid size={8}>
            <RFPStepper nonLinear activeStep={activeStep} alternativeLabel>
              {steps.map((item, index) => (
                <Step
                  key={item.name}
                  completed={isStepComplete(index)}
                  className={getStepClassName(index)}
                >
                  <StepButton onClick={handleStep(index)}>
                    {item.name}
                  </StepButton>
                </Step>
              ))}
            </RFPStepper>
          </Grid>
          <Grid size={4} pr={4}>
            <Stack direction="row" justifyContent="end">
              {!!timeFromLastUpdate && (
                <Stack pr={2} gap={0.8} direction="row" alignItems="center">
                  <HistoryOutlinedIcon color="action" fontSize="small" />

                  <Typography variant="body-medium" color="textSecondary">
                    Saved {timeFromLastUpdate} ago
                  </Typography>
                </Stack>
              )}
              <Stack gap={2} direction="row" alignItems="center">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    submitEvent("RFP Draft Saved Clicked", {
                      rfp_id: rfp[ID],
                      client_type: form[CLIENT_TYPE],
                    });
                    handleSaveDraft();
                  }}
                  disabled={isSavingDraftRfp}
                  startIcon={<SaveOutlinedIcon />}
                >
                  Save
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setIsDiscardDialogOpen(true)}
                  startIcon={<DeleteOutlineOutlinedIcon />}
                >
                  Discard
                </Button>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </RFPStepperPaper>
  );
};

export default CustomStepper;
